/**
 * Verificateur du NIP hors ligne (plan, section 1.7).
 *
 * - PBKDF2-SHA256, 310 000 iterations, par WebCrypto. Aucun repli : sans
 *   crypto.subtle, la verification echoue (la caisse ne demarre d'ailleurs
 *   pas sans WebCrypto, voir capabilities.js).
 * - Un sel aleatoire de 16 octets PROPRE AU POSTE (store/employees.js
 *   getOrCreatePinSalt), genere une fois, jamais lie au nom d'hote.
 * - Comme le NIP suffit, sans choisir son nom, le meme sel sert a tous les
 *   employes du poste : UNE derivation par essai, comparee ensuite a tous les
 *   verificateurs, en temps constant et sans sortie anticipee.
 *
 * Le NIP en clair ne sort jamais de ces fonctions : ni stocke, ni journalise,
 * ni place dans un message d'erreur.
 */

export const PIN_KDF = Object.freeze({ alg: 'PBKDF2-SHA256', iterations: 310_000, bits: 256 });
export const PIN_SALT_BYTES = 16;
export const PIN_PATTERN = /^\d{4,6}$/;

export class PinVerifierError extends Error {
    constructor(message, code) {
        super(message);
        this.name = 'PinVerifierError';
        this.code = code;
    }
}

/**
 * Tableau d'octets ? Sans `instanceof` : une valeur relue d'IndexedDB peut
 * venir d'un autre royaume JavaScript (tests Jest), avec son propre Uint8Array.
 */
export function isBytes(value) {
    return ArrayBuffer.isView(value) && Object.prototype.toString.call(value) === '[object Uint8Array]';
}

function getSubtle(subtle) {
    const s = subtle || (globalThis.crypto && globalThis.crypto.subtle);
    if (!s || typeof s.deriveBits !== 'function' || typeof s.importKey !== 'function') {
        throw new PinVerifierError('WebCrypto indisponible', 'NO_WEBCRYPTO');
    }
    return s;
}

/** Le NIP a-t-il la forme acceptee par le serveur (4 a 6 chiffres) ? */
export function isPinFormatValid(pin) {
    return typeof pin === 'string' && PIN_PATTERN.test(pin);
}

/**
 * Derive le verificateur d'un NIP avec le sel du poste.
 * @param {string} pin
 * @param {Uint8Array} salt
 * @param {SubtleCrypto} [subtle]
 * @returns {Promise<Uint8Array>} 32 octets
 */
export async function derivePinVerifier(pin, salt, subtle) {
    if (!isPinFormatValid(pin)) throw new PinVerifierError('NIP mal forme', 'NIP_FORMAT');
    if (!isBytes(salt) || salt.length !== PIN_SALT_BYTES) {
        throw new PinVerifierError('Sel du poste invalide', 'SEL_INVALIDE');
    }
    const s = getSubtle(subtle);
    const material = await s.importKey('raw', new TextEncoder().encode(pin), 'PBKDF2', false, ['deriveBits']);
    const bits = await s.deriveBits(
        { name: 'PBKDF2', hash: 'SHA-256', salt, iterations: PIN_KDF.iterations },
        material,
        PIN_KDF.bits
    );
    return new Uint8Array(bits);
}

/** Egalite de deux tableaux d'octets en temps constant (pour une longueur donnee). */
export function constantTimeEqual(a, b) {
    if (!isBytes(a) || !isBytes(b) || a.length !== b.length) return false;
    let diff = 0;
    for (let i = 0; i < a.length; i++) diff |= a[i] ^ b[i];
    return diff === 0;
}

/** Le verificateur d'un employe a-t-il ete calcule avec les reglages actuels ? */
export function hasUsableVerifier(employee) {
    return Boolean(employee
        && !employee.revoked
        && isBytes(employee.pin_verifier)
        && employee.pin_verifier.length === PIN_KDF.bits / 8
        && employee.pin_kdf
        && employee.pin_kdf.alg === PIN_KDF.alg
        && employee.pin_kdf.iterations === PIN_KDF.iterations);
}

/**
 * Compare un verificateur a ceux de tous les employes, sans sortie anticipee.
 * @returns {object[]} les employes qui correspondent (0, 1, ou plus si le NIP est partage)
 */
export function matchVerifier(verifier, employees) {
    const matches = [];
    for (const employee of employees) {
        const usable = hasUsableVerifier(employee);
        // Comparaison faite meme pour un verificateur inutilisable : meme
        // travail pour chaque employe.
        const equal = constantTimeEqual(verifier, usable ? employee.pin_verifier : new Uint8Array(verifier.length));
        if (usable && equal) matches.push(employee);
    }
    return matches;
}
